/**
 * `/agents/_shared` — RO-3-00's reachability proof for the shared render layer.
 *
 * ── Why this route exists, and what it is NOT ──
 *
 * RO-3-00 ports `client/shared/**`: components that ~7 section items import but
 * that no section item owns. Nothing in the route tree imports them yet, and that
 * is a measurement problem, not a cosmetic one: `pnpm --filter excellent
 * build:client` transformed **exactly 3634 modules before and after** the first
 * slice landed, because Rollup only walks what an entry reaches. A green Vite
 * build over a tree that does not contain your files is not evidence — it is the
 * same class of false green as a gate that scans zero of your files.
 *
 * So this route pulls every export of `client/shared/index.ts` into the graph, and
 * mounts the two that fetch their own data (`PerformancePanel`, `FleetSyncPanel` —
 * `perf.*` and `sync.*` are mounted on the Hono host) so the proof is a RENDER
 * against the real vault and not a compile.
 *
 * It is **not** a styleguide and not a product page. `/agents/_surface`
 * (RO-4-03) is the sibling precedent, including the leading-underscore segment:
 * `_shared` is a private segment in Next's App Router, so it can never collide
 * with a page `excellent/app/` grows (PRD D6 — both stacks run side by side).
 *
 * ── Delete me when I am redundant ──
 *
 * The moment RO-3-03 … RO-3-12 have ported routes that mount these components for
 * real, this route's job is done and it should be deleted along with
 * `/agents/_surface`. Do not grow it: adding a component here instead of to the
 * section route that needs it is how a scaffold becomes a shadow product.
 */

import { createRoute } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageHeader,
} from "@excellent/ui";
import { rootRoute } from "./__root";
import {
  AccountsView,
  ActivityLive,
  ContactsView,
  CreateMenu,
  CustomersScopeActions,
  FinanceScopeActions,
  FleetSyncPanel,
  FocusOnHash,
  ForecastView,
  HiringScopeActions,
  Logo,
  ModePlay,
  NewGoalDialog,
  ObjectsIndexClient,
  PerformancePanel,
  RenewalView,
  ReportsView,
  RoleSelect,
  RunAllAuditsButton,
  RunAnalysisButton,
  SectionColdStart,
  SignalsList,
  StrategicGoalsSurface,
  UnifiedIssuesList,
  WorkSurface,
  type ObjectKindRow,
} from "../shared";

/** There is no `objects.list` Operation yet, so this proof renders an honest empty input. */
const OBJECT_ROWS: ObjectKindRow[] = [];

function Section({ title, note, children }: { title: string; note: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="type-title">{title}</CardTitle>
        <p className="type-subtext text-muted-foreground">{note}</p>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function SharedRenderProofPage() {
  return (
    <div className="px-6 py-8 space-y-6">
      {/* Mounted so the export is exercised; renders nothing without a #focus- hash. */}
      <FocusOnHash />

      <PageHeader
        title="Shared render layer"
        description="RO-3-00 proof · client/shared/** imported through its published barrel and rendered in the Vite client. The Performance and Fleet-sync panels below are LIVE — they read the vault through the Hono host."
      />

      <Section
        title="Live · PerformancePanel"
        note="Self-fetching through perf.manifest + perf.history. An endpoint count here means browser → bundle → Hono → contract → core → disk all work for ported shared code."
      >
        <PerformancePanel hideAuditTitle />
      </Section>

      <Section
        title="Live · FleetSyncPanel"
        note="Self-fetching through sync.status, polling every 5s. 'Single-player' is the correct reading on a node with no primary configured."
      >
        <FleetSyncPanel />
      </Section>

      <Section
        title="Prop-driven · ObjectsIndexClient"
        note="Chromeless and empty — no objects.list Operation exists yet (RO-3-11 owes it)."
      >
        <ObjectsIndexClient rows={OBJECT_ROWS} scopeKinds={["money_account", "transaction"]} chromeless />
      </Section>

      <Section
        title="Prop-driven · the three *ScopeActions"
        note="Finance / Customers / Hiring wired onto the same ObjectsIndexClient + DataImportClient pair. The import legs 404 until L1 mounts /api/data-import/*."
      >
        <div className="flex flex-wrap items-center gap-3">
          <FinanceScopeActions rawRows={OBJECT_ROWS} />
          <CustomersScopeActions rawRows={[]} />
          <HiringScopeActions rawRows={[]} />
        </div>
      </Section>

      <Section
        title="Live · ModePlay"
        note="Self-fetching through play.queue on the live bus. An empty vault has no ranked actions, so the honest empty state is the correct render here."
      >
        <ModePlay title="What to do next" limit={3} />
      </Section>

      <Section
        title="Prop-driven · SectionColdStart + SignalsList"
        note="The section blank-state hero (Play launcher wired) and the home attention union. SignalsList's rows come from _home/home-screen.tsx, which is BLOCKED (shared-render.md §4)."
      >
        <SectionColdStart slug="finance" />
        <div className="mt-4">
          <SignalsList rows={[]} />
        </div>
      </Section>

      <Section
        title="RO-3-00g · cause-B shared clients"
        note="Ordinary ports unblocked by L2. WorkSurface/UnifiedIssuesList take prop rows; StrategicGoalsSurface self-fetches objectives.summary; CreateMenu/NewGoalDialog are chrome dialogs (closed)."
      >
        <div className="space-y-4">
          <WorkSurface items={[]} />
          <UnifiedIssuesList tasks={[]} extras={[]} verdicts={[]} />
          <div className="flex items-center gap-3">
            <CreateMenu />
            <NewGoalDialog roles={[]} />
          </div>
          {/* StrategicGoalsSurface self-fetches; keep it mounted so the contract is live. */}
          <StrategicGoalsSurface />
        </div>
      </Section>

      {/* RO-3-00d / #44471 — the five ported `_crm/*View` rewrites. Added here for
          the SAME reason the sections above exist, and it is not "growing the
          scaffold" in the sense this file's header warns about: no section route
          mounts a CRM view yet (RO-3-05 owns Customers), and without an entry that
          reaches them a green `build:client` says nothing about them — the exact
          false green the header names. Unlike the prop-driven sections, ALL FIVE
          are LIVE: `crm.views.{accounts,contacts,forecast,renewal,sequences,leads,support}` are mounted by
          this item and `crm.pipelines.list` + `crm.reports.*` were already
          mounted, so each one is a browser → bundle → Hono → contract → core →
          disk render. Delete these five alongside the rest of this route the
          moment RO-3-05 mounts them for real. */}
      <Section
        title="Live · AccountsView + ContactsView"
        note="Customers ▸ Accounts / Contacts. Self-fetching through crm.views.accounts + crm.views.contacts; the self-tagged org/person is excluded server-side. An empty vault's honest empty state is the correct render."
      >
        <AccountsView basePath="/customers" />
        <div className="mt-4">
          <ContactsView basePath="/customers" />
        </div>
      </Section>

      <Section
        title="Live · ForecastView"
        note="Customers ▸ Forecasting. Self-fetching through crm.views.forecast, which keeps the source's tableExists guard — a workspace where Sales/CRM never migrated shows zeros, not a 500."
      >
        <ForecastView basePath="/customers" />
      </Section>

      <Section
        title="Live · RenewalView"
        note="Customers ▸ Renewal. Self-fetching through crm.views.renewal — the retention-book subset of the customerParties read model, filtered server-side."
      >
        <RenewalView basePath="/customers" />
      </Section>

      <Section
        title="Live · ReportsView"
        note="CRM ▸ Reports. Composes FOUR already-mounted ops (crm.pipelines.list + the three crm.reports.*) and needed no new endpoint — the concrete payoff of RO-3-00b's 40-of-40 correction."
      >
        <ReportsView />
      </Section>

      <Section title="Leaves" note="Logo, RoleSelect, RunAnalysisButton, RunAllAuditsButton, ActivityLive — no data of their own.">
        <div className="flex flex-wrap items-center gap-4">
          <Logo size={24} className="text-foreground" />
          <div className="w-64">
            <RoleSelect
              roles={[{ name: "shipper", description: "Claims a task, ships it, hands off to review." }]}
              defaultRole="shipper"
            />
          </div>
          <RunAnalysisButton running={false} />
          <RunAllAuditsButton dueCount={0} onRunAll={async () => {}} />
        </div>
        <div className="mt-4">
          <ActivityLive seed={[]} />
        </div>
      </Section>
    </div>
  );
}

export const agentsSharedRenderProofRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/_shared",
  component: SharedRenderProofPage,
});
